import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AdminPasswordGate from "./AdminPasswordGate";

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionReady, setSessionReady] = useState(false);

  // Wait for initial auth check to finish before deciding to redirect
  useEffect(() => {
    if (!loading) {
      setSessionReady(true);
    }
  }, [loading]);

  // Only redirect AFTER we know the session state (prevents flash)
  useEffect(() => {
    if (!sessionReady) return;
    if (!user) {
      // Not logged in → send to admin login with redirect back here
      const params = new URLSearchParams();
      params.set("redirect", location.pathname);
      navigate(`/login?${params.toString()}`, { replace: true });
    }
  }, [sessionReady, user, navigate, location.pathname]);

  // While loading, show spinner (never show children)
  if (loading || !sessionReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-400">
          <i className="ri-loader-4-line animate-spin text-3xl mb-3 block"></i>
          <p className="text-sm">Checking session...</p>
        </div>
      </div>
    );
  }

  // Session check done, no user → redirect in progress, render nothing
  if (!user) return null;

  // User is logged in — pass through password gate
  return (
    <AdminPasswordGate>
      {children}
    </AdminPasswordGate>
  );
}
